/* =====================================================
   CART PROGRESS BAR — Currency-aware JS driver
   Thresholds are hardcoded in USD cents; converted to
   the customer's presentment currency at runtime via:
     1. window.Shopify.currency.rate  (Shopify Markets)
     2. window.Currency.rates[x]      (legacy multi-currency)
     3. 1:1 fallback for USD stores

   Mystery Gift auto-add / auto-remove:
     When cart total crosses GIFT_USD_CENTS upward  → add gift variant
     When cart total drops  below GIFT_USD_CENTS    → remove gift variant
     Gift line items are tagged with { _mystery_gift: '1' } (hidden property)
   ===================================================== */
(function () {
  'use strict';

  const SHIP_USD_CENTS = 8990;   // $89.90
  const GIFT_USD_CENTS = 16186;  // $161.86

  // ── Gift state ───────────────────────────────────────
  let _prevComplete = false;
  let _giftBusy     = false; // re-entrancy guard

  /* Returns presentment-currency units per 1 USD.
     E.g. 38.5 for TRY (meaning $1 = 38.5 TRY).
     Returns null when rate is unknown so we leave the
     Liquid-rendered state untouched. */
  function getRate(currency) {
    if (!currency || currency === 'USD') return 1;

    /* Prefer legacy currencies.js library (loaded via cart-drawer.liquid).
       Currency.rates[x] = USD per 1 unit of x  →  invert = units-per-USD.
       Same approach as the old theme (cart-drawer-extras.js). */
    if (window.Currency?.rates?.[currency]) {
      const r = parseFloat(window.Currency.rates[currency]);
      if (r > 0) return 1 / r;
    }

    /* Shopify Markets fallback: rate = display_currency / base_currency */
    if (
      window.Shopify?.currency?.active === currency &&
      window.Shopify.currency?.rate
    ) {
      const r = parseFloat(window.Shopify.currency.rate);
      if (r > 0) return r;
    }

    return null; /* unknown — don't override Liquid state */
  }

  function formatMoney(subunits, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2,
      }).format(subunits / 100);
    } catch (_) {
      return (subunits / 100).toFixed(2);
    }
  }

  // ── Gift helpers ─────────────────────────────────────

  function getGiftVariantId() {
    const cpb = document.querySelector('.cpb');
    return cpb ? (parseInt(cpb.dataset.giftVariant, 10) || 0) : 0;
  }

  /* Finds the mystery gift line item in the cart items array. */
  function findGiftLine(items, variantId) {
    if (!items || !variantId) return null;
    return items.find(
      item =>
        item.variant_id === variantId &&
        item.properties &&
        item.properties['_mystery_gift'] === '1'
    ) || null;
  }

  /* Publishes a cartUpdate PubSub event so Dawn's CartDrawerItems.onCartUpdate()
     refreshes cart-drawer-items and .cart-drawer__footer.
     Source 'balmy-gift' is intentionally different from 'cart-items' (Dawn skips
     that) and from 'mystery-gift' (our own listener skips that).
     The CPB element sits above <cart-drawer-items> in the DOM so onCartUpdate()
     never touches it — updateCPB state is preserved, no red flash. */
  function notifyDrawer(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'balmy-gift', cartData: cartData });
    }
  }

  /* Adds the mystery gift to cart (if not already present). */
  function doGiftAdd(variantId) {
    if (_giftBusy || !variantId) return;
    _giftBusy = true;

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        if (findGiftLine(cart.items, variantId)) {
          _giftBusy = false;
          updateCPB(cart.total_price, cart.currency || 'USD');
          notifyDrawer(cart);
          return;
        }

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { '_mystery_gift': '1' },
          }),
        })
          .then(r => r.json())
          .then(() => fetch('/cart.js').then(r => r.json()))
          .then(newCart => {
            _giftBusy = false;
            updateCPB(newCart.total_price, newCart.currency || 'USD');
            notifyDrawer(newCart);
          });
      })
      .catch(() => { _giftBusy = false; });
  }

  /* Removes the mystery gift from cart (if present). */
  function doGiftRemove(variantId) {
    if (_giftBusy || !variantId) return;
    _giftBusy = true;

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const line = findGiftLine(cart.items, variantId);
        if (!line) {
          _giftBusy = false;
          return;
        }

        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        })
          .then(r => r.json())
          .then(newCart => {
            _giftBusy = false;
            updateCPB(newCart.total_price, newCart.currency || 'USD');
            notifyDrawer(newCart);
          });
      })
      .catch(() => { _giftBusy = false; });
  }

  /* Exposed globally so balmy-product-drum.js can call it directly after
     refreshing the cart drawer DOM without going through a PubSub event. */
  function updateCPB(total, currency) {
    const cpb = document.querySelector('.cpb');
    if (!cpb) return;

    const rate = getRate(currency);
    if (rate === null) return; /* rely on Liquid render */

    const shipT = Math.round(SHIP_USD_CENTS * rate);
    const giftT = Math.round(GIFT_USD_CENTS * rate);
    const pct   = giftT > 0 ? Math.min(100, Math.round(total * 100 / giftT)) : 0;

    /* Green progress fill (grows left → right) */
    const progress = cpb.querySelector('.cpb__progress');
    if (progress) progress.style.width = pct + '%';

    /* Gray cover that shrinks from right */
    const fill = cpb.querySelector('.cpb__fill');
    if (fill) fill.style.left = pct + '%';

    /* Complete state */
    const complete = total >= giftT;
    cpb.classList.toggle('cpb--complete', complete);

    /* Node active states */
    const nodes = cpb.querySelectorAll('.cpb__node');
    if (nodes[0]) nodes[0].classList.toggle('cpb__node--on', total >= shipT);
    if (nodes[1]) nodes[1].classList.toggle('cpb__node--on', complete);

    /* Message */
    const msg = cpb.querySelector('.cpb__msg');
    if (msg) {
      if (complete) {
        msg.innerHTML = 'Free Shipping + Mystery Gift unlocked!';
      } else if (total >= shipT) {
        msg.innerHTML =
          'Spend <strong>' +
          formatMoney(giftT - total, currency) +
          '</strong> more to unlock your <strong>Mystery Gift</strong>!';
      } else {
        msg.innerHTML =
          'Spend <strong>' +
          formatMoney(shipT - total, currency) +
          '</strong> more for <strong>Free Shipping</strong>';
      }
    }

    /* Gift auto-add / auto-remove — only fires on state TRANSITIONS.
       Skipped when _giftBusy (re-entrancy guard) or no change. */
    if (!_giftBusy && complete !== _prevComplete) {
      const variantId = getGiftVariantId();
      if (variantId) {
        if (complete) doGiftAdd(variantId);
        else          doGiftRemove(variantId);
      }
    }
    _prevComplete = complete;
  }

  /* Initial render: read values baked into data attributes by Liquid */
  function initFromDom() {
    const cpb = document.querySelector('.cpb');
    if (!cpb) return;
    const total    = parseInt(cpb.dataset.cartTotal    || '0', 10);
    const currency = cpb.dataset.cartCurrency || 'USD';
    updateCPB(total, currency);
  }

  /* Global hook — lets balmy-product-drum.js trigger a CPB update after
     it refreshes the cart drawer DOM via its own Sections API fetch. */
  window.balmyCPBUpdate = updateCPB;

  /* Try immediately from DOM attributes (fast path; may be skipped if rate unknown yet) */
  initFromDom();

  /* After all resources load, fetch /cart.js as authoritative ground truth.
     This handles cases where getRate() returned null on the initial call because
     currency rates hadn't finished loading (currencies.js async XHR, Shopify Markets
     late injection, etc.). Only fetches when .cpb is in the DOM (non-empty cart). */
  function syncFromCartJS() {
    if (!document.querySelector('.cpb')) return;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCPB(cart.total_price, cart.currency || 'USD'); })
      .catch(function () {});
  }

  if (document.readyState === 'complete') {
    syncFromCartJS();
  } else {
    window.addEventListener('load', syncFromCartJS);
  }

  /* MutationObserver — .cpb her yeniden render edildiğinde CPB'yi günceller.
     Kapsanan 3 senaryo:
       1. Boş sepetten ilk ürün ekleme → .cpb DOM'a girer
       2. BPD ATC / renderContents() → tüm #CartDrawer innerHTML yenilenir (.cpb dahil)
       3. balmyRefreshCartDrawer() veya CartDrawerItems.onCartUpdate() →
          .drawer__inner veya .cpb'nin children'ları yenilenir
     updateCPB()'nin kendi DOM değişikliklerinde TETIKLENMEZ çünkü:
       — .cpb__msg içindeki text/strong değişikliklerinde m.target = .cpb__msg (cpb değil)
       — progress/fill style değişiklikleri attribute change (childList değil) */
  (function () {
    var _syncTimer = null;
    function debouncedSync() {
      clearTimeout(_syncTimer);
      _syncTimer = setTimeout(syncFromCartJS, 50);
    }

    function isCPBAffected(mutations) {
      for (var mi = 0; mi < mutations.length; mi++) {
        var m = mutations[mi];
        var an = m.addedNodes;
        // Case 1: .cpb kendisi veya içinde .cpb olan bir element eklendi
        for (var i = 0; i < an.length; i++) {
          var n = an[i];
          if (n.nodeType !== 1) continue;
          if ((n.classList && n.classList.contains('cpb')) ||
              (n.querySelector && n.querySelector('.cpb'))) return true;
        }
        // Case 2: .cpb'nin children'ları değişti (balmyRefreshCartDrawer o.innerHTML=n.innerHTML)
        if (m.target && m.target.nodeType === 1 &&
            m.target.classList && m.target.classList.contains('cpb') &&
            an.length > 0) return true;
      }
      return false;
    }

    var _obs = new MutationObserver(function (mutations) {
      if (!document.querySelector('.cpb')) return;
      if (isCPBAffected(mutations)) debouncedSync();
    });

    _obs.observe(document.getElementById('CartDrawer') || document.body, {
      childList: true,
      subtree: true,
    });
  })();

  /* Dawn PubSub — fires after each cart change.
     cart-items source (quantity / remove): cartData is the full cart response from
       /cart/change.js — total_price and currency are always accurate. Use directly.
     product-form source (add to cart): cartData is a line-item response; total_price
       is the LINE ITEM price, NOT the cart total. Fetch /cart.js for the real total.
     mystery-gift source: our own events — skip to avoid double-processing. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'mystery-gift') return;

      var cd = event && event.cartData;
      if (cd && typeof cd.item_count === 'number' && typeof cd.total_price === 'number') {
        updateCPB(cd.total_price, cd.currency || 'USD');
        return;
      }
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { updateCPB(cart.total_price, cart.currency || 'USD'); })
        .catch(function () {});
    });
  }
})();
